import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Users, Building, AlertCircle, ArrowUpCircle, Database, FileText } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) => (Number(v) || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

export default function AnagraficheView({ onPromoted }) {
  const [partyType, setPartyType] = useState('supplier');
  const [parties, setParties] = useState([]);
  const [temporary, setTemporary] = useState([]);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteForm, setPromoteForm] = useState({ snapshot_name: '', name: '', vat: '', category: '', notes: '', link_existing: true });

  useEffect(() => { load(); }, [partyType]);

  const load = async () => {
    try {
      const [p, t] = await Promise.all([
        api.get(`/treasury/parties?party_type=${partyType}`),
        api.get(`/treasury/temporary-parties?party_type=${partyType}`),
      ]);
      setParties(p.data || []);
      setTemporary(t.data || []);
    } catch { toast.error('Errore caricamento'); }
  };

  const openPromote = (snapshotRow) => {
    setPromoteForm({
      snapshot_name: snapshotRow.name,
      name: snapshotRow.name,
      vat: '',
      category: '',
      notes: `Promosso da fornitore/cliente temporaneo (${snapshotRow.invoice_count} fattur${snapshotRow.invoice_count === 1 ? 'a' : 'e'}, ${fmt(snapshotRow.total_amount)})`,
      link_existing: true,
    });
    setPromoteOpen(true);
  };

  const handlePromote = async (e) => {
    e.preventDefault();
    if (!promoteForm.name.trim()) { toast.error('Ragione sociale obbligatoria'); return; }
    try {
      const r = await api.post('/treasury/parties/promote', { party_type: partyType, ...promoteForm });
      toast.success(`Promosso! Ricollegate ${r.data.relinked_invoices} fatture e ${r.data.relinked_movements} scadenze.`);
      setPromoteOpen(false);
      await load();
      onPromoted && onPromoted();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore promozione'); }
  };

  const erpParties = parties.filter(p => p.source === 'erp');
  const treasuryParties = parties.filter(p => p.source === 'treasury');
  const label = partyType === 'client' ? 'Clienti' : 'Fornitori';

  return (
    <div className="space-y-4" data-testid="treasury-anagrafiche-view">
      <Tabs value={partyType} onValueChange={setPartyType}>
        <TabsList>
          <TabsTrigger value="supplier" data-testid="anag-tab-supplier"><Building className="w-4 h-4 mr-1" />Fornitori</TabsTrigger>
          <TabsTrigger value="client" data-testid="anag-tab-client"><Users className="w-4 h-4 mr-1" />Clienti</TabsTrigger>
        </TabsList>

        <TabsContent value={partyType} className="space-y-4">
          {/* Temporary parties banner */}
          {temporary.length > 0 && (
            <Card className="border-amber-300 bg-amber-50/40" data-testid="anag-temporary-card">
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <h3 className="text-sm font-semibold text-amber-900">{label} temporanei ({temporary.length})</h3>
                </div>
                <p className="text-xs text-amber-800">
                  Questi nominativi sono presenti solo come <strong>snapshot sulle fatture</strong>, non in anagrafica. Promuovili per riutilizzarli e ricollegare automaticamente le fatture esistenti.
                </p>
                <table className="w-full text-xs mt-2">
                  <thead><tr className="border-b border-amber-300 text-left text-[10px] uppercase tracking-wider text-amber-700">
                    <th className="py-1.5 px-2">Nome</th>
                    <th className="py-1.5 px-2 text-center">Fatture</th>
                    <th className="py-1.5 px-2 text-right">Totale</th>
                    <th className="py-1.5 px-2">Ultima fattura</th>
                    <th className="py-1.5 px-2 w-32"></th>
                  </tr></thead>
                  <tbody>
                    {temporary.map((t, idx) => (
                      <tr key={t.name} className="border-b border-amber-200/60" data-testid={`anag-temp-row-${idx}`}>
                        <td className="py-1.5 px-2 font-medium text-amber-900">{t.name}</td>
                        <td className="py-1.5 px-2 text-center">{t.invoice_count}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{fmt(t.total_amount)}</td>
                        <td className="py-1.5 px-2 font-mono text-[10px]">{t.last_invoice_date || '—'}</td>
                        <td className="py-1.5 px-2 text-right">
                          <Button size="sm" onClick={() => openPromote(t)} data-testid={`anag-promote-btn-${idx}`}>
                            <ArrowUpCircle className="w-3 h-3 mr-1" />Promuovi
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Treasury parties */}
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-emerald-700" />
                <h3 className="text-sm font-semibold">{label} Tesoreria ({treasuryParties.length})</h3>
              </div>
              {treasuryParties.length === 0 ? (
                <p className="text-xs text-slate-400 py-3 text-center">Nessuno. Crea da &quot;Nuova anagrafica&quot;.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {treasuryParties.map(p => (
                    <div key={p.id} className="border rounded p-2 flex items-center gap-2 text-xs">
                      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px]">Tesoreria</Badge>
                      <span className="truncate flex-1 font-medium">{p.name}</span>
                      {p.vat && <span className="text-[10px] text-slate-400 font-mono">{p.vat}</span>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ERP parties */}
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-4 h-4 text-purple-700" />
                <h3 className="text-sm font-semibold">{label} ERP ({erpParties.length})</h3>
              </div>
              {erpParties.length === 0 ? (
                <p className="text-xs text-slate-400 py-3 text-center">Nessuno in anagrafica ERP.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                  {erpParties.map(p => (
                    <div key={p.id} className="border rounded p-2 flex items-center gap-2 text-xs">
                      <Badge className="bg-purple-50 text-purple-700 border border-purple-200 text-[9px]">ERP</Badge>
                      <span className="truncate flex-1 font-medium">{p.name}</span>
                      {p.code && <span className="text-slate-400">({p.code})</span>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Promote dialog */}
      <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <DialogContent className="max-w-md" data-testid="anag-promote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowUpCircle className="w-5 h-5" />Promuovi in anagrafica</DialogTitle>
            <DialogDescription>
              Crea il {partyType === 'client' ? 'cliente' : 'fornitore'} in anagrafica Tesoreria e ricollega tutte le fatture/scadenze esistenti.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePromote} className="space-y-3">
            <div>
              <Label>Ragione sociale *</Label>
              <Input value={promoteForm.name} onChange={e => setPromoteForm({ ...promoteForm, name: e.target.value })} required data-testid="anag-promote-name" />
              <p className="text-[10px] text-slate-500 mt-0.5">Snapshot originale: <code className="bg-slate-100 px-1 rounded">{promoteForm.snapshot_name}</code></p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>P.IVA</Label><Input value={promoteForm.vat} onChange={e => setPromoteForm({ ...promoteForm, vat: e.target.value })} /></div>
              <div><Label>Categoria merceologica</Label><Input value={promoteForm.category} onChange={e => setPromoteForm({ ...promoteForm, category: e.target.value })} /></div>
            </div>
            <div><Label>Note</Label><Textarea rows={2} value={promoteForm.notes} onChange={e => setPromoteForm({ ...promoteForm, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPromoteOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="anag-promote-submit"><ArrowUpCircle className="w-4 h-4 mr-1" />Promuovi e ricollega</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
